import cn from 'classnames';
import PropTypes from 'prop-types';

import { ADD_SHIP_ADDRESS, DELETE_SHIP_ADDRESS, EDIT_SHIP_ADDRESS } from 'constants/checkoutButtons';
import { ADDRESS_STEP, CHECKOUT_STEP_MAP } from 'constants/checkoutFlow';

import AddressForm from 'components/checkout/caap/address/AddressForm';
import BackButton from 'components/checkout/caap/BackButton';
import CheckboxInput from 'components/checkout/caap/CheckboxInput';
import ModalOverlay from 'components/checkout/caap/ModalOverlay';
import css from 'styles/components/checkout/caap/address/newAddressModal.scss';

const NewAddressModal = (componentParams, { testId }) => {
  const {
    address,
    hasInvalidAddress,
    isAlsoBilling = false,
    isEditAddressStep,
    isFetchingCheckoutData,
    isNewPrimary,
    onAddressFieldChange,
    onCountryDropdownChange,
    onDeleteAddressClick,
    onShipToThisAddressClick,
    onToggleIsAlsoBillingClick,
    onToggleIsUseAsDefaultAddressClick,
    pageHistory,
    shouldShowUseAsBillingAddress,
    shouldShowIsNewPrimary,
    showAsModal,
    showPoBoxHelperText,
    onCloseModalOverlayClick
  } = componentParams;

  const containerStyle = cn(css.newAddressContainer, { [css.inlineFormContainer]: !showAsModal });
  const shipToButtonId = address.addressId ? EDIT_SHIP_ADDRESS : ADD_SHIP_ADDRESS;
  const sectionTitleStyles = cn({ [css.titleWhenBackBtn]: pageHistory && pageHistory.includes('address') });
  const continueButtonStyles = cn(
    css.continueButtonContainer,
    { [css.editAddressStep]: isEditAddressStep }
  );

  return (
    <div data-test-id={testId('newOrEditAddressContainer')} className={containerStyle}>
      <ModalOverlay onCloseModalOverlayClick={onCloseModalOverlayClick} showAsModal={showAsModal}>

        {
          showAsModal
            ? <div className={css.modalTitleContainer}>
              { address.addressId ? 'Update your shipping address' : 'Enter a new shipping address' }
              <button
                type="button"
                className={css.closeButton}
                data-test-id={testId('closeModal')}
                onClick={onCloseModalOverlayClick}
                onKeyPress={onCloseModalOverlayClick}
                role="link"
                tabIndex="0"
                title="Close Modal"></button>
            </div>
            : null
        }

        {
          pageHistory && pageHistory.includes('address')
            ? <BackButton href={CHECKOUT_STEP_MAP[ADDRESS_STEP]} />
            : null
        }

        <div className={css.showOnlySmallScreen}><h1 className={sectionTitleStyles}>{ address.addressId ? 'Update your shipping address' : 'Enter a shipping address' }</h1></div>

        <form method="post" onSubmit={onShipToThisAddressClick}>
          <div className={css.contentWrapper}>
            <AddressForm
              address={address}
              hasInvalidAddress={hasInvalidAddress}
              isEditAddressStep={isEditAddressStep}
              onCountryDropdownChange={onCountryDropdownChange}
              onFieldChange={onAddressFieldChange}
              showPoBoxHelperText={showPoBoxHelperText} />

            {
              shouldShowUseAsBillingAddress
                ? <CheckboxInput
                  clearAfter={true}
                  clearAfterWithSpace={true}
                  isChecked={isAlsoBilling}
                  onChange={onToggleIsAlsoBillingClick}
                  name="isAlsoBilling"
                  dataTestId="isAlsoBilling">
                Use address for billing
                </CheckboxInput>
                : null
            }

            {
              shouldShowIsNewPrimary
                ? <CheckboxInput
                  isChecked={!!isNewPrimary}
                  onChange={onToggleIsUseAsDefaultAddressClick}
                  name="isUseAsDefaultAddress"
                  dataTestId="isUseAsDefaultAddress">
                  Use as my default address.
                </CheckboxInput>
                : null
            }
          </div>

          <div className={continueButtonStyles}>
            <button
              className={css.checkoutPrimaryButton}
              data-test-id={testId('shipToNewAddress')}
              disabled={isFetchingCheckoutData}
              id={shipToButtonId}
              onClick={onShipToThisAddressClick}
              type="submit">
              Ship to this address
            </button>
            {
              address.addressId
                ? <button
                  className={css.checkoutSecondaryButton}
                  data-test-id={testId('deleteNewShippingAddress')}
                  disabled={isFetchingCheckoutData}
                  id={DELETE_SHIP_ADDRESS}
                  onClick={() => onDeleteAddressClick(address.addressId)}
                  type="button">
                  Delete this address
                </button>
                : null
            }
          </div>
        </form>
      </ModalOverlay>
    </div>
  );
};

NewAddressModal.contextTypes = {
  testId: PropTypes.func
};

export default NewAddressModal;
